/**
 * @file: useTaskListWithSearch.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useSearchContext } from '@euler/app/tv/components/dashboard/SearchContext';
import {
  TaskListTabInfo,
  TaskListTabType,
} from '@euler/app/tv/components/dashboard/TaskListToolbar';
import { TaskListMode } from '@euler/app/tv/functions/useDashboardTaskListMetrics';
import { taskKeyExtractor } from '@euler/app/tv/functions/utils';
import { useRefreshFeedback } from '@euler/functions/useRefreshFeedback';
import {
  SearchParams,
  useSearchResults,
} from '@euler/functions/useSearchResults';
import { TaskListSearchParams } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn, usePrevious } from '@euler/utils/hooks';
import { useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, interval } from 'rxjs';

export const useTaskListWithSearch = (props: {
  tabItems: TaskListTabInfo[];
  initialTabType: TaskListTabType;
  defaultLimit?: number;
  refreshInterval?: number;
  preferredMode?: TaskListMode;
  query$?: BehaviorSubject<string>;
  debounce?: number;
  shouldPerformSearch?: (params: TaskListSearchParams) => boolean;
}) => {
  const playRefreshFeedback = useRefreshFeedback();

  const { query$: defaultQuery$ } = useSearchContext();

  const query$ = useMemo(
    () => props.query$ ?? defaultQuery$,
    [props.query$, defaultQuery$],
  );

  const { taskService } = useServiceFactory();

  const [preferredMode, setPreferredMode] = useState<TaskListMode>(
    props.preferredMode ?? 'horizontal-complete',
  );
  const [activeTabType, setActiveTabType] = useState<TaskListTabType>(
    props.initialTabType,
  );

  const activeTab = props.tabItems.find(x => x.type === activeTabType)!;

  const limit = props.defaultLimit ?? 24;

  const search = usePersistFn(async (searchPrams: SearchParams) => {
    const params: TaskListSearchParams = {
      keyword: searchPrams.keyword,
      ...activeTab.toSearchParams(),
    };
    if (props.shouldPerformSearch?.(params) === false) {
      __DEV__ && console.log('skipped searching tasks with params: ', params);
      return [];
    }
    try {
      __DEV__ && console.log('searching tasks with params: ', params);
      const results = await taskService.list(
        params,
        searchPrams.offset,
        searchPrams.limit,
      );
      return results;
    } catch (e) {
      console.error(e);
      throw e;
    }
  });

  const { data, status, fetchMore, replace } = useSearchResults({
    query$,
    search,
    limit,
    keyExtractor: taskKeyExtractor,
    debounced: props.debounce ?? 500,
  });
  const isRefreshing = status.isRefreshing;

  const isRefreshPrev = usePrevious(isRefreshing);

  useEffect(() => {
    if (!isRefreshing && isRefreshPrev) {
      void playRefreshFeedback();
    }
  }, [isRefreshing, isRefreshPrev, playRefreshFeedback]);

  const previousTabType = usePrevious(activeTabType);

  const onRefresh = usePersistFn(() => {
    replace(query$.getValue()).catch(onErrorIgnore);
  });

  useEffect(() => {
    if (previousTabType != null && previousTabType !== activeTabType) {
      onRefresh();
    }
  }, [activeTabType, onRefresh, previousTabType, query$, replace]);

  useEffect(() => {
    // refresh the dashboard task list every 5 seconds.
    const sub = interval(props.refreshInterval ?? 60 * 5 * 1000).subscribe(
      () => {
        onRefresh();
      },
    );
    return () => sub.unsubscribe();
  }, [onRefresh, props.refreshInterval]);

  return {
    data,
    activeTabType,
    activeTab,
    preferredMode,
    isRefreshing,
    isLoading:
      status.isLoading && !status.isRefreshing && !status.loadedDataLength,
    hasMore: status.hasMore,
    error: status.error,
    onRefresh,
    onFetchMore: fetchMore,
    onPreferredModeChange: setPreferredMode,
    onActiveTabTypeChange: setActiveTabType,
  };
};
